import React from "react";
import {Button} from "primereact/button";
import TwemojiText from "../../system/components/twemoji-text";
import {useTranslate} from "../../system/useTranslate";
import {reactionMembershipIdentity} from "../../models/chat-message/chat-message-reaction-state";

export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "😡", "🎉", "🔥"];

export function reactionMembershipKey(reaction) {
    return reactionMembershipIdentity(reaction?.user);
}

export function reactionActorName(reaction, fallback = "") {
    const member = reaction?.user?.member;
    return member?.fullname
        || [member?.firstname, member?.lastname].filter(Boolean).join(" ")
        || member?.username
        || fallback;
}

export function summarizeReactions(reactions, currentMembership) {
    const currentKey = String(currentMembership?.documentId ?? currentMembership?.id ?? "");
    const summaries = new Map();

    (Array.isArray(reactions) ? reactions : []).forEach((reaction) => {
        if (!reaction?.emoji || !reactionMembershipKey(reaction)) return;
        const current = summaries.get(reaction.emoji) || {
            emoji: reaction.emoji,
            count: 0,
            reactions: [],
            reactedByCurrentUser: false,
        };
        current.count += 1;
        current.reactions.push(reaction);
        current.reactedByCurrentUser = current.reactedByCurrentUser
            || (currentKey && reactionMembershipKey(reaction) === currentKey);
        summaries.set(reaction.emoji, current);
    });

    return [...summaries.values()];
}

export function nextReactionEmoji(reactions, currentMembership, selectedEmoji) {
    const currentKey = String(currentMembership?.documentId ?? currentMembership?.id ?? "");
    const currentReaction = (Array.isArray(reactions) ? reactions : []).find((reaction) => (
        reactionMembershipKey(reaction) === currentKey
    ));
    return currentReaction?.emoji === selectedEmoji ? null : selectedEmoji;
}

function ChatMessageReactions({message, currentMembership, onReact, loading = false}) {
    const translate = useTranslate();
    const summaries = summarizeReactions(message?.reactions, currentMembership);
    if (!summaries.length) return null;

    return (
        <div className="rtl flex flex-wrap align-items-center gap-1 mt-1">
            {summaries.map((summary) => (
                <Button
                    key={summary.emoji}
                    type="button"
                    text
                    disabled={loading}
                    className={`text-xs px-2 py-1 gap-1 border-round-xl border-1 ${summary.reactedByCurrentUser ? "surface-200 border-primary text-primary" : "surface-100 border-200 text-700"}`}
                    label={(
                        <span className="flex align-items-center gap-1">
                            <TwemojiText imageClassName="inline-block vertical-align-middle w-1rem h-1rem">
                                {summary.emoji}
                            </TwemojiText>
                            <span>{summary.count}</span>
                        </span>
                    )}
                    aria-label={`${translate("inbox.message.reaction.summary")} ${summary.emoji} ${summary.count}`}
                    onClick={() => onReact?.(
                        message,
                        nextReactionEmoji(message?.reactions, currentMembership, summary.emoji)
                    )}
                />
            ))}
        </div>
    );
}

export default ChatMessageReactions;
